'use strict';

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { useAssets, useUIState } from '../contexts';
import Popup from '@components/Widgets/Popup.react';
import CopyToClipboard from '@components/Dashboard/Header/CalendarSync/CopyToClipboard.react';
import { getConfig } from '@utils/Env';

/**
 * MultiSharePopup - Modern functional React component for sharing multiple meals
 *
 * Replaces the legacy class-based MultiSharePopup with hooks and modern context.
 * Uses the same CSS classes and structure for visual consistency.
 */
const MultiSharePopup = ({
    meals = [],
    buttonText = 'share',
    popupText = 'To share, copy and paste this URL...',
    className = '',
    positionClassName = '',
    onClick
}) => {
    // Get assets and UI state from modern context instead of legacy context
    const { recipes, foods } = useAssets();
    const { isMobile } = useUIState();

    // Generate share details for all meals
    const shareDetails = useMemo(() => {
        const wwwHost = getConfig('www_host');
        const validMeals = [];

        // First, collect all valid meals with their data
        meals.forEach((meal) => {
            let name = '';
            let url = '';

            // Handle recipe meals
            if (meal.recipe_uuid && recipes && recipes[meal.recipe_uuid]) {
                const recipe = recipes[meal.recipe_uuid];
                name = recipe.title || recipe.name || 'Recipe';
                url = `https://${wwwHost}/recipes/${meal.recipe_uuid}`;
            }
            // Handle food meals
            else if (meal.food_uuid && foods && foods[meal.food_uuid]) {
                const food = foods[meal.food_uuid];
                name = food.pretty_name || food.name || 'Food';
                url = `https://${wwwHost}/foods/${meal.food_uuid}`;
            }

            // Only add if we have both name and URL
            if (name && url) {
                validMeals.push({ name, url });
            }
        });

        // Then, format names based on valid meal count
        return validMeals.map(({ name, url }, index) => ({
            name: validMeals.length > 1 ? `DISH ${index + 1}: ${name}` : name,
            url
        }));
    }, [meals, recipes, foods]);

    // Render button content
    const buttonContent = (
        <>
            {!isMobile && <i className="feather feather-share" />}
            {buttonText && <em>{buttonText}</em>}
        </>
    );

    // Render popup content
    const popupContent = (
        <div className="share-popup-content">
            <p>{popupText}</p>
            {shareDetails.map(({ name, url }, key) => (
                <div className="copy-url-container" key={key}>
                    <div className="title">{name}</div>
                    <CopyToClipboard value={url} />
                </div>
            ))}
        </div>
    );

    return (
        <Popup
            className={`share-popup multi-share-popup ${className}`}
            positionClassName={positionClassName}
            button={buttonContent}
            onClick={onClick}
        >
            {popupContent}
        </Popup>
    );
};

MultiSharePopup.propTypes = {
    meals: PropTypes.array,
    buttonText: PropTypes.string,
    popupText: PropTypes.string,
    className: PropTypes.string,
    positionClassName: PropTypes.string,
    onClick: PropTypes.func,
};

export default MultiSharePopup;
